import { ITestCardBase } from "./ITestCardBase";
import {
  ManagedBy,
  ParsedTestCard,
  PrimaryAction,
  SampleType,
  TestCard,
  TestCardStatus,
  TestStatus,
  TestType,
} from "@app/provider/testData/types";
import { IntlShape } from "react-intl/src/types";
import { messages } from "@app/provider/testData/messages";
import { isHorizonOrPanorama } from "@app/utils/orderHelpers";
import { SimpleOrderWorkflowState } from "@app/provider/order";
import { Utils } from "@natera/material/lib/date";
import { getEstimatedResultsDeliveryDate } from "@app/utils";

export abstract class TestCardBase implements ITestCardBase {
  protected getDescriptionForUpcomingTest(
    testCard: TestCard,
    intl: IntlShape
  ): string | undefined {
    if (testCard.managedBy === ManagedBy.NATERA) {
      if (testCard.isProReach) {
        return intl.formatMessage(messages.testProsperaCardDescOrderedNatera);
      } else {
        return intl.formatMessage(messages.testCardBloodDrawTestOrdered);
      }
    }
    return intl.formatMessage(messages.testCardDescOrderedClinic);
  }

  protected getDescriptionForTestOrdered(
    testCard: TestCard,
    intl: IntlShape
  ): string | undefined {
    if (
      testCard.simpleOrderWorkflowState &&
      isHorizonOrPanorama(testCard.testType) &&
      testCard.managedBy === ManagedBy.CLINIC &&
      ((testCard.simpleOrderWorkflowState ===
        SimpleOrderWorkflowState.PATIENT_ACCESSED &&
        testCard.isOrderInSignedOrdersTable) ||
        [
          SimpleOrderWorkflowState.PATIENT_SIGNED,
          SimpleOrderWorkflowState.KIT_ORDERED,
          SimpleOrderWorkflowState.COMPLETED,
        ].includes(testCard.simpleOrderWorkflowState))
    ) {
      return intl.formatMessage(
        messages.testCardDescOrderedConnectPatientSigned
      );
    }
    if (testCard.nextStatus === TestCardStatus.SAMPLE_RECEIVED) {
      if (testCard.managedBy === ManagedBy.CLINIC) {
        return intl.formatMessage(
          messages.testCardDescOrderedConnectManagedByClinic
        );
      }
      return intl.formatMessage(messages.testCardDescOrderedConnect);
    }
    return intl.formatMessage(messages.testCardDescOrderedConnectDraft);
  }

  protected getDescriptionForKitShipped(
    testCard: TestCard,
    intl: IntlShape
  ): string | undefined {
    if (testCard.sampleType === SampleType.BLOOD) {
      return intl.formatMessage(messages.testCardDescShipped);
    }
    if (testCard.sampleType === SampleType.SALIVA) {
      return intl.formatMessage(messages.testCardDescSubmitKit);
    }
    return undefined;
  }

  protected getDescriptionForSampleReceived(
    testCard: TestCard,
    intl: IntlShape
  ): string | undefined {
    if (
      [
        TestType.EMPOWER,
        TestType.HORIZON,
        TestType.PANORAMA,
        TestType.SINGLE_GENE_NIPT,
        TestType.SIGNATERA,
        TestType.VISTARA,
        TestType.LATITUDE,
      ].includes(testCard.testType) &&
      testCard.sampleReceivedDate
    ) {
      const estimatedResultsDeliveryDate = Utils.getUSLocaleDate(
        getEstimatedResultsDeliveryDate(
          testCard,
          new Date(testCard.sampleReceivedDate),
          testCard.isSignateraSubsequent
        )
      );
      return intl.formatMessage(messages.testCardDescSampleReceived, {
        estimatedResultsDate: estimatedResultsDeliveryDate,
      });
    }
    return intl.formatMessage(messages.testCardDescSample);
  }

  protected abstract getDescriptionForSamplesReceived(
    testCard: TestCard,
    intl: IntlShape
  ): string | undefined;

  protected getDescriptionForResultReady(
    testCard: TestCard,
    intl: IntlShape
  ): string | undefined {
    return intl.formatMessage(messages.testCardDescResultReadyReview);
  }

  protected getDescriptionForTestComplete(
    testCard: TestCard,
    intl: IntlShape
  ): string | undefined {
    if (testCard.viewStatus === TestStatus.VIEW_WITH_PHYSICIAN) {
      return intl.formatMessage(messages.testCardDescComplete);
    }
    return intl.formatMessage(messages.testCardDescResultReadyReview);
  }

  protected getDescriptionForRedrawNeeded(
    testCard: TestCard,
    intl: IntlShape
  ): string | undefined {
    return intl.formatMessage(messages.testCardDescRedrawNeeded);
  }

  protected getDescriptionForPatientResultReleaseDelayed(
    testCard: TestCard,
    intl: IntlShape
  ): string {
    const resultsDelayedUntilDate = Utils.getUSLocaleDate(
      new Date(testCard.patientResultAvailableAt!)
    );
    return intl.formatMessage(
      messages.testCardDescPatientResultReleaseDelayed,
      {
        resultsDelayedUntilDate,
      }
    );
  }

  public getDescription(
    testCard: TestCard,
    intl: IntlShape
  ): string | undefined {
    switch (testCard.status) {
      case TestCardStatus.UPCOMING_TEST:
        return this.getDescriptionForUpcomingTest(testCard, intl);
      case TestCardStatus.TEST_ORDERED:
        return this.getDescriptionForTestOrdered(testCard, intl);
      case TestCardStatus.KIT_SHIPPED:
        return this.getDescriptionForKitShipped(testCard, intl);
      case TestCardStatus.APPT_REQUESTED:
        return intl.formatMessage(messages.testCardDescApptRequested);
      case TestCardStatus.APPT_CONFIRMED:
        return intl.formatMessage(messages.testCardDescApptConfirmed);
      case TestCardStatus.SAMPLE_RECEIVED:
        return this.getDescriptionForSampleReceived(testCard, intl);
      case TestCardStatus.SAMPLES_RECEIVED:
        return this.getDescriptionForSamplesReceived(testCard, intl);
      case TestCardStatus.PROCESSING:
        return this.getDescriptionForPatientResultReleaseDelayed(
          testCard,
          intl
        );
      case TestCardStatus.RESULT_READY:
        return this.getDescriptionForResultReady(testCard, intl);
      case TestCardStatus.TEST_COMPLETE:
        return this.getDescriptionForTestComplete(testCard, intl);
      case TestCardStatus.CANCELLED_TEST:
        return intl.formatMessage(messages.testCardDescCancelled);
      case TestCardStatus.EXPIRED_TEST:
        return intl.formatMessage(messages.testCardDescExpired);
      case TestCardStatus.REDRAW_NEEDED:
        return this.getDescriptionForRedrawNeeded(testCard, intl);
      case TestCardStatus.NEW_TISSUE_NEEDED:
        return intl.formatMessage(messages.testCardDescAdditionalTissueNeeded);
    }
  }

  protected abstract getPrimaryActionForUpcomingTest(
    testCard: TestCard
  ): PrimaryAction | undefined;

  protected abstract getPrimaryActionForTestOrdered(
    testCard: TestCard
  ): PrimaryAction | undefined;

  protected abstract getPrimaryActionForKitShipped(
    testCard: TestCard
  ): PrimaryAction | undefined;

  protected getPrimaryActionForRedrawNeeded(
    testCard: TestCard
  ): PrimaryAction | undefined {
    return undefined;
  }

  protected getPrimaryAction(testCard: TestCard): PrimaryAction | undefined {
    switch (testCard.status) {
      case TestCardStatus.UPCOMING_TEST:
        return this.getPrimaryActionForUpcomingTest(testCard);
      case TestCardStatus.TEST_ORDERED:
        return this.getPrimaryActionForTestOrdered(testCard);
      case TestCardStatus.KIT_SHIPPED:
        return this.getPrimaryActionForKitShipped(testCard);
      case TestCardStatus.APPT_REQUESTED:
      case TestCardStatus.APPT_CONFIRMED:
        return PrimaryAction.APPOINTMENT_DETAIL;
      case TestCardStatus.RESULT_READY:
        return PrimaryAction.VIEW_YOUR_RESULTS;
      case TestCardStatus.TEST_COMPLETE:
        return PrimaryAction.VIEW_RESULT_PAGE;
      case TestCardStatus.REDRAW_NEEDED:
        return this.getPrimaryActionForRedrawNeeded(testCard);
      default:
        return undefined;
    }
  }

  public buildCard(testCard: TestCard, intl: IntlShape): ParsedTestCard {
    return {
      uid: testCard.uid,
      orderUidPublic: testCard.orderUidPublic,
      testType: testCard.testType,
      createdAt: testCard.createdAt,
      accessedResultAt: testCard.accessedResultAt,
      status: testCard.status,
      nextStatus: testCard.nextStatus,
      description: this.getDescription(testCard, intl),
      primaryAction: this.getPrimaryAction(testCard),
      organType: testCard.organType,
      sampleType: testCard.sampleType,
      sampleReceivedDate: testCard.sampleReceivedDate,
      trackingNumber: testCard.trackingNumber,
      reportGender: testCard.reportGender,
      caPnsSupplemental: testCard.caPnsSupplemental,
      resultCode: testCard.resultCode,
      fetalSex: testCard.fetalSex,
      resultReleasedAt: testCard.resultReleasedAt,
      latestResultDocumentUid: testCard.latestResultDocumentUid,
      cancelledAt: testCard.cancelledAt,
      isProReach: testCard.isProReach,
      clinic: testCard.clinic,
      isComboOrder: testCard.isComboOrder,
      managedBy: testCard.managedBy,
      drawCoordinator: testCard.drawCoordinator,
      isNevaEnabled: testCard.isNevaEnabled,
      isOrderInSignedOrdersTable: testCard.isOrderInSignedOrdersTable,
      simpleOrderWorkflowState: testCard.simpleOrderWorkflowState,
      isSignateraSubsequent: testCard.isSignateraSubsequent,
      isEmpowerStatPanelIncluded: testCard.isEmpowerStatPanelIncluded,
      isLatestReleasedResultStat: testCard.isLatestReleasedResultStat,
      isStatResultAlreadyViewed: testCard.isStatResultAlreadyViewed,
      isTfMrdActive: testCard.isTfMrdActive,
      tfMrdOrderingOption: testCard.tfMrdOrderingOption,
      clinicalInfo: testCard.clinicalInfo,
      isPatientResultReleaseDelayed: testCard.isPatientResultReleaseDelayed,
      patientResultAvailableAt: testCard.patientResultAvailableAt,
      // Pass through i18nKeys for components (title, statusText, nextStatus)
      i18nKeys: testCard.i18nKeys,
    };
  }
}
